import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { formatCurrency } from '@mahawi/eshop-common/src/format-currency';
import { numberCompare } from '@mahawi/eshop-common/src/number-compare';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import { EOrderStatus } from '@mahawi/eshop-common/src/types';
import { Button, InputNumber, Select, Space, Tooltip } from 'antd';
import { type ColumnGroupType, type ColumnType } from 'antd/es/table';
import { type Key } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import DetailPageHeader from '../components/detail-page-header';
import Link from '../components/link';
import Table from '../components/table';
import { EDateFormat } from '../fragments/date-format';
import { getDateRangeColumnFilter } from '../fragments/date-range-column-filter';
import { getColumnSearch } from '../fragments/table-get-column-search';
import { renderUpdatedAtMessage } from '../fragments/updated-at-message';
import { useCurrencyMap } from '../hooks/use-currency-map';
import { type RootState } from '../reducers';
import {
  ordersLoadByOrderStatusCodes,
  ordersReset,
} from '../reducers/orders/reducer';
import { type IAdminOrder } from '../reducers/orders/types';
import { SPACING } from '../theme/tokens';

const ALL_STATUSES: EOrderStatus[] = Object.values(EOrderStatus);

interface DataType extends IAdminOrder {
  key: string;
  priceWithTax: number;
  vatAmount: number;
}

function ReportAccountingContainer(): React.ReactElement {
  const dispatch = useDispatch();
  const Orders = useSelector((state: RootState) => state.Orders);
  const Currency = useSelector((state: RootState) => state.Currency);

  const [selectedStatuses, setSelectedStatuses] = useState<EOrderStatus[]>([
    EOrderStatus.PAID,
    EOrderStatus.DELIVERED,
    EOrderStatus.SHIPPED,
  ]);
  const [ordersLimit, setOrdersLimit] = useState<number>(500);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

  const currencyByIso = useCurrencyMap(Currency.currencies);

  const formatPrice = useCallback(
    (value: number, iso: string): string => {
      const currencyType = currencyByIso.get(iso);
      return currencyType ? formatCurrency(value, currencyType) : String(value);
    },
    [currencyByIso],
  );

  const dataSource = useMemo((): DataType[] => {
    if (!Orders.orders || Orders.inProcess) return [];
    return Orders.orders.map((order: IAdminOrder): DataType => {
      const priceWithTax: number =
        order.currencyPrice.pricesWithTax[0]?.price ?? 0;
      const vatAmount: number =
        priceWithTax - order.currencyPrice.priceWithoutTax;
      return {
        ...order,
        key: order.uuid,
        priceWithTax,
        vatAmount,
      };
    });
  }, [Orders.orders, Orders.inProcess, Orders.updatedAt]);

  const selectedData = useMemo((): DataType[] => {
    if (selectedRowKeys.length === 0) return dataSource;
    const keySet = new Set(selectedRowKeys);
    return dataSource.filter((r: DataType): boolean => keySet.has(r.key));
  }, [dataSource, selectedRowKeys]);

  const exportCsv = useCallback((): void => {
    const headers = [
      'UUID',
      'Email',
      'Total with VAT',
      'VAT amount',
      'Total without VAT',
      'Items count',
      'Payment vendor ID',
      'Invoice numbers',
      'Currency',
      'Date',
    ];

    const escape = (v: string | number): string => {
      const s = String(v);
      if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const rows = selectedData.map((r: DataType): string =>
      [
        r.uuid,
        r.billingAddress.email,
        r.priceWithTax,
        r.vatAmount,
        r.currencyPrice.priceWithoutTax,
        r.productsCount,
        r.thirdPartyPaymentIds.join('; '),
        r.invoices.map((inv) => inv.fakturoidNumber).join('; '),
        r.currencyPrice.isoCode,
        dayjs(r.createdAt).format(EDateFormat.DATE),
      ]
        .map(escape)
        .join(','),
    );

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `accounting-report-${dayjs().format(EDateFormat.DATE)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [selectedData]);

  const columns = useMemo(
    () => [
      {
        title: 'UUID',
        dataIndex: 'uuid',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.uuid, b.uuid),
        ...getColumnSearch<DataType>('uuid'),
        render: (_: unknown, record: DataType): React.ReactElement => (
          <Link to={`/order/${record.uuid}`} label={record.uuid} openInNewTab />
        ),
        width: '18%',
      },
      {
        title: 'Email',
        dataIndex: ['billingAddress', 'email'],
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.billingAddress.email, b.billingAddress.email),
        ...getColumnSearch<DataType>('billingAddress'),
        render: (_: unknown, record: DataType): string =>
          record.billingAddress.email,
        width: '18%',
      },
      {
        title: 'Total with VAT',
        dataIndex: 'priceWithTax',
        sorter: (a: DataType, b: DataType): number =>
          numberCompare(a.priceWithTax, b.priceWithTax),
        render: (value: number, record: DataType): string =>
          formatPrice(value, record.currencyPrice.isoCode),
        align: 'right' as const,
        width: '11%',
      },
      {
        title: 'VAT amount',
        dataIndex: 'vatAmount',
        sorter: (a: DataType, b: DataType): number =>
          numberCompare(a.vatAmount, b.vatAmount),
        render: (value: number, record: DataType): string =>
          formatPrice(value, record.currencyPrice.isoCode),
        align: 'right' as const,
        width: '9%',
      },
      {
        title: 'Total without VAT',
        dataIndex: ['currencyPrice', 'priceWithoutTax'],
        sorter: (a: DataType, b: DataType): number =>
          numberCompare(
            a.currencyPrice.priceWithoutTax,
            b.currencyPrice.priceWithoutTax,
          ),
        render: (_: unknown, record: DataType): string =>
          formatPrice(
            record.currencyPrice.priceWithoutTax,
            record.currencyPrice.isoCode,
          ),
        align: 'right' as const,
        width: '11%',
      },
      {
        title: 'Items',
        dataIndex: 'productsCount',
        sorter: (a: DataType, b: DataType): number =>
          numberCompare(a.productsCount, b.productsCount),
        align: 'right' as const,
        width: '6%',
      },
      {
        title: 'Payment vendor ID',
        dataIndex: 'thirdPartyPaymentIds',
        ...getColumnSearch<DataType>('thirdPartyPaymentIds'),
        render: (_: unknown, record: DataType): string =>
          record.thirdPartyPaymentIds.join(', '),
        width: '10%',
      },
      {
        title: 'Invoice numbers',
        dataIndex: 'invoices',
        ...getColumnSearch<DataType>('invoices'),
        render: (_: unknown, record: DataType): string =>
          record.invoices.map((inv) => inv.fakturoidNumber).join(', '),
        width: '10%',
      },
      {
        title: 'Date',
        dataIndex: 'createdAt',
        sorter: (a: DataType, b: DataType): number =>
          dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
        ...getDateRangeColumnFilter<DataType>(
          (record: DataType): Date => record.createdAt,
        ),
        render: (value: Date): string => dayjs(value).format(EDateFormat.DATE),
        width: '9%',
      },
    ],
    [formatPrice],
  );

  return (
    <>
      <DetailPageHeader title="Accounting report" />

      <Space size={SPACING.lg} align="center">
        <Select
          mode="multiple"
          style={{ minWidth: 360 }}
          placeholder="Select order statuses"
          value={selectedStatuses}
          onChange={setSelectedStatuses}
          options={ALL_STATUSES.map((s: EOrderStatus) => ({
            label: s,
            value: s,
          }))}
        />

        <InputNumber
          value={ordersLimit}
          min={1}
          max={5000}
          onChange={(v: number | null): void => setOrdersLimit(Number(v))}
        />

        <Button
          type={!Orders.orders ? 'primary' : 'default'}
          disabled={Orders.inProcess || selectedStatuses.length === 0}
          onClick={(): void => {
            dispatch(ordersReset());
            dispatch(
              ordersLoadByOrderStatusCodes(selectedStatuses, ordersLimit),
            );
          }}
        >
          <ReloadOutlined /> Load
        </Button>

        {renderUpdatedAtMessage(
          Orders.updatedAt,
          Orders.inProcess,
          (): boolean => Orders.orders !== undefined,
        )}

        <Tooltip
          title={
            selectedRowKeys.length === 0
              ? 'Export all records'
              : `Export ${selectedRowKeys.length} selected`
          }
        >
          <Button
            type="primary"
            disabled={dataSource.length === 0}
            onClick={exportCsv}
          >
            <DownloadOutlined />
            {selectedRowKeys.length === 0
              ? `Export CSV (${dataSource.length})`
              : `Export CSV (${selectedRowKeys.length})`}
          </Button>
        </Tooltip>
      </Space>

      <Table
        columns={columns as (ColumnGroupType<object> | ColumnType<object>)[]}
        dataSource={dataSource}
        isLoading={Orders.inProcess}
        emptyText="No orders found"
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys,
          onChange: (keys: Key[]): void => setSelectedRowKeys(keys),
        }}
      />
    </>
  );
}

export default ReportAccountingContainer;
